import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const SocialProofSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = {
    en: {
      title: "Trusted by Industry Leaders",
      subtitle: "Recognition and testimonials from founders, media, and industry experts",
      mediaTitle: "Recent Media Coverage",
      awardsTitle: "Industry Recognition",
      testimonialsTitle: "Founder Testimonials"
    },
    de: {
      title: "Vertraut von Branchenführern",
      subtitle: "Anerkennung und Testimonials von Gründern, Medien und Branchenexperten",
      mediaTitle: "Aktuelle Medienberichterstattung",
      awardsTitle: "Branchenanerkennung",
      testimonialsTitle: "Gründer-Testimonials"
    },
    fr: {
      title: "Approuvé par les leaders de l\'industrie",
      subtitle: "Reconnaissance et témoignages de fondateurs, médias et experts de l'industrie",
      mediaTitle: "Couverture médiatique récente",
      awardsTitle: "Reconnaissance de l\'industrie",
      testimonialsTitle: "Témoignages de fondateurs"
    },
    es: {
      title: "Confiado por líderes de la industria",
      subtitle: "Reconocimiento y testimonios de fundadores, medios y expertos de la industria",
      mediaTitle: "Cobertura mediática reciente",
      awardsTitle: "Reconocimiento de la industria",
      testimonialsTitle: "Testimonios de fundadores"
    }
  };

  const mediaFeatures = [
  {
    outlet: "TechCrunch Europe",
    logo: "https://images.unsplash.com/photo-1668026848162-a0fa1d1d55dd",
    logoAlt: "TechCrunch Europe logo with bold red and black typography",
    headline: "REXFEL leads €15M Series A in Hamburg-based FinTech",
    date: "October 15, 2025",
    type: "Feature Article"
  },
  {
    outlet: "Financial Times",
    logo: "https://images.unsplash.com/photo-1730818876455-abd3318be279",
    logoAlt: "Financial Times logo with classic serif font in salmon pink color",
    headline: "European VCs embrace crypto-traditional investment hybrid",
    date: "October 12, 2025",
    type: "Market Analysis"
  },
  {
    outlet: "Handelsblatt",
    logo: "https://images.unsplash.com/photo-1578512943063-66c595f1bb27",
    logoAlt: "Handelsblatt logo with modern German business newspaper branding",
    headline: "Hamburg wird zum deutschen FinTech-Zentrum",
    date: "October 10, 2025",
    type: "Industry Report"
  }];


  const awards = [
  {
    title: "Best European VC 2025",
    organization: "EU Startup Awards",
    year: "2025",
    icon: "Award"
  },
  {
    title: "FinTech Investor of the Year",
    organization: "German Startup Association",
    year: "2024",
    icon: "Trophy"
  },
  {
    title: "Innovation Partner Award",
    organization: "Hamburg Chamber of Commerce",
    year: "2024",
    icon: "Star"
  }];


  const testimonials = [
  {
    quote: "REXFEL didn\'t just invest in our company—they became true strategic partners. Their expertise in scaling European startups globally was invaluable during our Series A growth phase.",
    author: "Sarah Chen",
    position: "CEO & Founder",
    company: "Oneplacetwostories",
    avatar: "https://images.unsplash.com/photo-1597621969117-1a305d3e0c68",
    avatarAlt: "Professional headshot of Asian woman with shoulder-length black hair in navy blazer"
  },
  {
    quote: "The combination of traditional VC wisdom and digital-first innovation that REXFEL brings is exactly what we needed. They understand both furniture retail and emerging tech trends.",
    author: "Marcus Weber",
    position: "Co-Founder & CTO",
    company: "Mobellex",
    avatar: "https://images.unsplash.com/photo-1678484124459-3db080862303",
    avatarAlt: "Professional headshot of Caucasian man with short brown hair and beard in gray suit"
  },
  {
    quote: "REXFEL's network opened doors we couldn't have accessed alone. Their Hamburg base gave us the perfect European launchpad for our FinTech platform.",
    author: "Elena Rodriguez",
    position: "Founder & CEO",
    company: "Tiksaviems",
    avatar: "https://images.unsplash.com/photo-1651659802584-08bf160743dc",
    avatarAlt: "Professional headshot of Hispanic woman with long dark hair in white business shirt"
  }];


  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  const currentContent = content?.[currentLanguage];

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16 scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {currentContent?.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {currentContent?.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Media Coverage */}
          <div className="scroll-reveal">
            <h3 className="text-xl font-bold text-foreground mb-6 flex items-center">
              <Icon name="Newspaper" size={24} className="mr-3 text-primary" />
              {currentContent?.mediaTitle}
            </h3>
            
            <div className="space-y-4">
              {mediaFeatures?.map((feature, index) =>
              <div
                key={index}
                className={`bg-background rounded-brand p-4 border border-border hover:shadow-brand transition-brand hover-lift stagger-${index + 1}`}>

                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-6 overflow-hidden rounded-brand-xs flex-shrink-0">
                      <Image
                      src={feature?.logo}
                      alt={feature?.logoAlt}
                      className="w-full h-full object-cover" />

                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-primary font-medium mb-1">
                        {feature?.outlet} • {feature?.type}
                      </div>
                      <h4 className="text-sm font-medium text-foreground leading-tight mb-2">
                        {feature?.headline}
                      </h4>
                      <div className="text-xs text-muted-foreground">
                        {feature?.date}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Industry Awards */}
          <div className="scroll-reveal">
            <h3 className="text-xl font-bold text-foreground mb-6 flex items-center">
              <Icon name="Award" size={24} className="mr-3 text-accent" />
              {currentContent?.awardsTitle}
            </h3>
            
            <div className="space-y-4">
              {awards?.map((award, index) =>
              <div
                key={index}
                className={`bg-background rounded-brand p-4 border border-border hover:shadow-brand transition-brand hover-lift stagger-${index + 1}`}>

                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full flex items-center justify-center">
                      <Icon name={award?.icon} size={20} className="text-accent" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{award?.title}</h4>
                      <p className="text-sm text-muted-foreground">{award?.organization}</p>
                      <div className="text-xs text-primary font-medium">{award?.year}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Trust Metrics */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="bg-background rounded-brand p-4 text-center border border-border">
                <div className="text-2xl font-bold text-success">98%</div>
                <div className="text-xs text-muted-foreground">Founder Satisfaction</div>
              </div>
              <div className="bg-background rounded-brand p-4 text-center border border-border">
                <div className="text-2xl font-bold text-primary">4.9/5</div>
                <div className="text-xs text-muted-foreground">Partner Rating</div>
              </div>
            </div>
          </div>

          {/* Founder Testimonials */}
          <div className="scroll-reveal">
            <h3 className="text-xl font-bold text-foreground mb-6 flex items-center">
              <Icon name="MessageSquare" size={24} className="mr-3 text-success" />
              {currentContent?.testimonialsTitle}
            </h3>
            
            <div className="relative">
              <div className="bg-background rounded-brand p-6 border border-border shadow-brand">
                <div className="mb-4">
                  <Icon name="Quote" size={32} className="text-primary/20" />
                </div>
                
                <blockquote className="text-muted-foreground leading-relaxed mb-6">
                  "{testimonials?.[activeTestimonial]?.quote}"
                </blockquote>
                
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden">
                    <Image
                      src={testimonials?.[activeTestimonial]?.avatar}
                      alt={testimonials?.[activeTestimonial]?.avatarAlt}
                      className="w-full h-full object-cover" />

                  </div>
                  <div>
                    <div className="font-medium text-foreground">
                      {testimonials?.[activeTestimonial]?.author}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {testimonials?.[activeTestimonial]?.position}
                    </div>
                    <div className="text-xs text-primary font-medium">
                      {testimonials?.[activeTestimonial]?.company}
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial Navigation */}
              <div className="flex justify-center space-x-2 mt-4">
                {testimonials?.map((_, index) =>
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-2 h-2 rounded-full transition-brand ${
                  index === activeTestimonial ?
                  'bg-primary' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'}`
                  } />

                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default SocialProofSection;